import type { ElementType } from "react"
import { useMemo } from "react"
import { Table, Skeleton, Box, HStack, Text, Icon } from "@chakra-ui/react"
import { FaBolt, FaPlug, FaFlask, FaGasPump, FaBatteryFull } from "react-icons/fa6"
import type { FieldGroup, FieldDefinition } from "./fieldGroups"

export interface BandedTableProps {
  /** Data rows from the reporting API */
  rows: Record<string, any>[]
  /** Currently selected field keys */
  selectedFields: string[]
  /** Canonical field groups with color/icon/fields */
  groups: FieldGroup[]
  /** Whether data is currently loading */
  loading: boolean
}

/** Map group icon names to react-icons components */
const ICON_MAP: Record<string, ElementType> = {
  bolt: FaBolt,
  plug: FaPlug,
  flask: FaFlask,
  "gas-pump": FaGasPump,
  battery: FaBatteryFull,
}

interface VisibleGroup {
  group: FieldGroup
  fields: FieldDefinition[]
}

/**
 * BandedTable renders a table with two-row grouped column headers.
 *
 * - Top row: group bands (colored, with icon + label) spanning their columns
 * - Bottom row: individual column headers for each field
 * - Only groups with at least one selected field get a band header
 * - Columns ordered by canonical group order (Period first)
 * - Loading state renders skeleton rows
 */
export function BandedTable({ rows, selectedFields, groups, loading }: BandedTableProps) {
  /** Compute visible groups and their selected fields, maintaining canonical order */
  const visibleGroups: VisibleGroup[] = useMemo(() => {
    return groups
      .map((group) => {
        const fields = group.fields.filter((field) => selectedFields.includes(field.key))
        return { group, fields }
      })
      .filter(({ fields }) => fields.length > 0)
  }, [groups, selectedFields])

  /** Flat list of all visible fields in canonical order */
  const orderedFields: FieldDefinition[] = useMemo(() => {
    return visibleGroups.flatMap(({ fields }) => fields)
  }, [visibleGroups])

  return (
    <Table.ScrollArea>
      <Table.Root variant="outline" interactive size="sm">
        <Table.Header>
          {/* Top row: group band headers */}
          <Table.Row>
            {visibleGroups.map(({ group, fields }) => {
              const IconComponent = ICON_MAP[group.icon]
              return (
                <Table.ColumnHeader
                  key={group.key}
                  colSpan={fields.length}
                  textAlign="center"
                  borderBottomWidth="2px"
                  borderBottomColor={group.color || "gray.200"}
                  bg={group.color ? `${group.color}/5` : "gray.50"}
                >
                  <HStack gap={1} justifyContent="center">
                    {IconComponent && (
                      <Icon color={group.color}>
                        <IconComponent />
                      </Icon>
                    )}
                    <Text fontSize="xs" fontWeight="semibold" color={group.color || "gray.600"}>
                      {group.label}
                    </Text>
                  </HStack>
                </Table.ColumnHeader>
              )
            })}
          </Table.Row>

          {/* Bottom row: individual column headers */}
          <Table.Row>
            {orderedFields.map((field) => (
              <Table.ColumnHeader key={field.key} textAlign="right" fontSize="xs">
                {field.label}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {loading ? (
            [...Array(5)].map((_, index) => (
              <Table.Row key={index}>
                <Table.Cell colSpan={orderedFields.length || 1}>
                  <Skeleton height="20px" width="100%" />
                </Table.Cell>
              </Table.Row>
            ))
          ) : rows.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={orderedFields.length || 1} textAlign="center">
                <Box py={4}>
                  <Text color="gray.500">No data available for the selected time range</Text>
                </Box>
              </Table.Cell>
            </Table.Row>
          ) : (
            rows.map((row, rowIndex) => (
              <Table.Row key={row.bucket_start ?? rowIndex}>
                {orderedFields.map((field) => {
                  const value = row[field.key]
                  let display: string
                  if (value == null) {
                    display = "—"
                  } else if (typeof value === "number") {
                    display = value.toFixed(2)
                  } else {
                    display = String(value)
                  }
                  return (
                    <Table.Cell key={field.key} textAlign="right" fontSize="sm">
                      {display}
                    </Table.Cell>
                  )
                })}
              </Table.Row>
            ))
          )}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  )
}

export default BandedTable
